import { type LTLQuickQuoteResponseQuoteError } from 'lib/api/getLTLQuickQuotes';
import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { mapTaiLtlQuoteErrorsToUserMessage } from './ltlQuoteErrorMessages';

export const LTL_ZERO_QUOTE_OUTCOME = {
  Empty: 'empty',
  ProviderFailure: 'provider_failure',
} as const;

export type LtlZeroQuoteOutcome =
  (typeof LTL_ZERO_QUOTE_OUTCOME)[keyof typeof LTL_ZERO_QUOTE_OUTCOME];

type LtlQuoteErrorMessageMapper = (
  quoteErrors: MaybeUndef<LTLQuickQuoteResponseQuoteError[]>
) => MaybeUndef<string>;

export type LtlProviderOutcomeConfig = {
  emptyQuotesMessage: string;
  providerFailureMessage: string;
  systemFailureMessage: string;
  quoteErrorMessageMapper?: LtlQuoteErrorMessageMapper;
};

export const LTL_LEGACY_EMPTY_QUOTES_MESSAGE = 'No quotes have been returned.';

export const LTL_LEGACY_SYSTEM_FAILURE_MESSAGE = 'Failed to fetch LTL Quotes';

// Per-TMS LTL zero-quote UX. Register a TMS here to enable distinct empty vs provider-failure
// messaging; unlisted TMS integrations keep legacy Quick Quote flow.
const LTL_PROVIDER_OUTCOME_CONFIG: Partial<
  Record<TMS, LtlProviderOutcomeConfig>
> = {
  [TMS.ThreeG]: {
    emptyQuotesMessage: LTL_LEGACY_EMPTY_QUOTES_MESSAGE,
    providerFailureMessage:
      "TMS didn't return LTL Quotes. Try again or escalate if it continues.",
    systemFailureMessage:
      'LTL quotes fetching is taking longer than expected. Please try again.',
  },
  [TMS.Tai]: {
    emptyQuotesMessage: LTL_LEGACY_EMPTY_QUOTES_MESSAGE,
    providerFailureMessage:
      "We couldn't get LTL quotes. Please verify your shipment details and try again.",
    systemFailureMessage: LTL_LEGACY_SYSTEM_FAILURE_MESSAGE,
    quoteErrorMessageMapper: mapTaiLtlQuoteErrorsToUserMessage,
  },
};

export function getLtlProviderOutcomeConfig(
  tms: Undef<TMS>
): Undef<LtlProviderOutcomeConfig> {
  if (!tms) {
    return undefined;
  }

  return LTL_PROVIDER_OUTCOME_CONFIG[tms];
}
